import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type ServiceYearScaleItem = {
  role: string;
  minYears: number;
  maxYears: number;
  days: number;
  _id?: string;
};

export interface AnnualLeave {
  enabled: boolean;
  baseAllocation: number;
  proRateByStartDate: boolean;
  scaleByServiceYears: boolean;
  serviceYearScale: ServiceYearScaleItem[];
}

export interface SickLeave {
  enabled: boolean;
  accrualRate: number;
  maxAccrual: number;
  gracePeriodDays: number;
  requireCertification: boolean;
  certificationAfterDays: number;
}

export interface PressingNecessity {
  enabled: boolean;
  daysPerYear: number;
  proRated: boolean;
}

export interface CompassionateLeave {
  enabled: boolean;
  maxDaysPerYear: number;
}

export interface BereavementLeave {
  enabled: boolean;
  maxDaysPerOccurrence: number;
}

export interface EducationLeave {
  enabled: boolean;
  maxDaysPerYear: number;
}

export interface ProfessionalDevelopmentLeave {
  enabled: boolean;
  maxDaysPerYear: number;
}

export interface CulturalSpiritualLeave {
  enabled: boolean;
  maxDaysPerYear: number;
}

export interface LeaveWithoutPay {
  enabled: boolean;
  requiresApproval: boolean;
}

export interface ApprovalWorkflow {
  requireSupervisorApproval: boolean;
  flagToDirectorThreshold: number;
  autoApproveUnderDays: number;
}

// Multi-level configuration support
export type LeaveConfigLevel = "organization" | "department" | "program" | "user";

export interface LeaveConfigurationDetail {
  org: string;
  year?: number;
  level?: LeaveConfigLevel;
  entityId?: string;
  annualLeave: AnnualLeave;
  sickLeave: SickLeave;
  pressingNecessity: PressingNecessity;
  compassionateLeave: CompassionateLeave;
  bereavementLeave: BereavementLeave;
  juryDutyLeave?: { enabled: boolean };
  electionLeave?: { enabled: boolean; maxDaysPerYear?: number };
  bandBusinessLeave?: { enabled: boolean };
  governanceLeave?: { enabled: boolean };
  educationLeave?: EducationLeave;
  professionalDevelopmentLeave?: ProfessionalDevelopmentLeave;
  culturalSpiritualLeave?: CulturalSpiritualLeave;
  leaveWithoutPay?: LeaveWithoutPay;
  approvalWorkflow?: ApprovalWorkflow;
  createdBy?: string;
  _id: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export type LeaveConfigResponse = {
  success: boolean;
  data: LeaveConfigurationDetail | null;
  message?: string;
};

export type ResolvedLeaveConfigResponse = {
  success: boolean;
  data: LeaveConfigurationDetail;
  source?: {
    level: LeaveConfigLevel;
    entityId?: string;
    configId?: string;
  };
  leaveTypeSources?: Record<string, { level: LeaveConfigLevel; entityId?: string }>;
};

export type AllLeaveConfigsResponse = {
  success: boolean;
  data: {
    organization: LeaveConfigurationDetail | null;
    departments: LeaveConfigurationDetail[];
    programs: LeaveConfigurationDetail[];
    users: LeaveConfigurationDetail[];
  };
};

// Fetch config at a specific level (default: organization)
const fetchLeaveConfiguration = async (
  id: string,
  level?: LeaveConfigLevel,
  entityId?: string,
  year?: number
): Promise<LeaveConfigResponse> => {
  const params = new URLSearchParams();
  if (level) params.append("level", level);
  if (entityId) params.append("entityId", entityId);
  if (year != null) params.append("year", String(year));
  const queryStr = params.toString() ? `?${params.toString()}` : "";
  const { data } = await customAxios.get<LeaveConfigResponse>(
    `/leave-configuration/${id}${queryStr}`
  );
  return data;
};

// Fetch resolved/effective config for a specific user
const fetchResolvedLeaveConfiguration = async (
  orgId: string,
  userId: string
): Promise<ResolvedLeaveConfigResponse> => {
  const { data } = await customAxios.get<ResolvedLeaveConfigResponse>(
    `/leave-configuration/${orgId}/resolved/${userId}`
  );
  return data;
};

// Fetch all configs at all levels for admin view
const fetchAllLeaveConfigurations = async (
  orgId: string
): Promise<AllLeaveConfigsResponse> => {
  const { data } = await customAxios.get<AllLeaveConfigsResponse>(
    `/leave-configuration/${orgId}/all`
  );
  return data;
};

// Hook: Get config at a specific level
export const useGetLeaveConfiguration = (
  id?: string | null,
  level?: LeaveConfigLevel,
  entityId?: string,
  year?: number
) =>
  useQuery<LeaveConfigResponse, AxiosError>(
    ["leaveConfiguration", id, level, entityId, year],
    () => {
      if (!id) throw new Error("No leave configuration id provided");
      return fetchLeaveConfiguration(id, level, entityId, year);
    },
    { enabled: !!id }
  );

// Hook: Get resolved config for a user
export const useGetResolvedLeaveConfiguration = (
  orgId?: string | null,
  userId?: string | null
) =>
  useQuery<ResolvedLeaveConfigResponse, AxiosError>(
    ["leaveConfigurationResolved", orgId, userId],
    () => {
      if (!orgId || !userId) throw new Error("orgId and userId are required");
      return fetchResolvedLeaveConfiguration(orgId, userId);
    },
    { enabled: !!orgId && !!userId }
  );

// Hook: Get all configs at all levels (admin view)
export const useGetAllLeaveConfigurations = (orgId?: string | null) =>
  useQuery<AllLeaveConfigsResponse, AxiosError>(
    ["leaveConfigurationAll", orgId],
    () => {
      if (!orgId) throw new Error("No org id provided");
      return fetchAllLeaveConfigurations(orgId);
    },
    { enabled: !!orgId }
  );
